import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, RootState } from '../../redux';
import {
    Ability, Icon,
    LastActivity, LastReleases, WhatsNew,
    DashboardBriefing, FavoriteProjectsStrip, MyTasksFocus,
    ChangesTeamReview, SupportIndicators, TriageChanges
} from '../../components';
import { loadUsers, getStatistics, updateUser, useAppDispatch } from '../../redux';
import {
    DASHBOARD_WIDGET_IDS,
    isWidgetVisible,
    normalizeDashboardPreferences,
    preferencesForPreset,
    toggleWidgetHidden,
    type DashboardPreferences,
    type DashboardPreset,
    type DashboardWidgetId,
} from '../../utils/dashboardWidgetPrefs';

import './Dashboard.css';

function Dashboard() {
    const { t }    = useTranslation();
    const dispatch = useAppDispatch();

    const { user } = useAppSelector((state: RootState) => state.auth);
    const { statistics, stale } = useAppSelector((state: RootState) => state.statistics || {});
    const [prefsOpen, setPrefsOpen] = useState(false);

    const prefs: DashboardPreferences = useMemo(() => {
        const normalized = normalizeDashboardPreferences(user?.dashboardPreferences);
        // Role-based default when the user still has the schema default (development).
        if (
            user?.role === 'client'
            && (!user?.dashboardPreferences?.preset
                || user.dashboardPreferences.preset === 'development'
                || user.dashboardPreferences.preset === 'delivery')
            && (user?.dashboardPreferences?.hiddenWidgets || []).length === 0
        ) {
            return preferencesForPreset('client');
        }
        return normalized;
    }, [user?.dashboardPreferences, user?.role]);

    useEffect(() => {
        dispatch(loadUsers());
        dispatch(getStatistics(["changes"]));
    }, [dispatch]);

    useEffect(() => {
        if (stale)
            dispatch(getStatistics(["changes"]));
    }, [stale, dispatch]);

    const hasTasksColumn = user?.can('change:start');
    const show = (id: DashboardWidgetId) => isWidgetVisible(prefs, id);

    const showLeftColumn = !!(hasTasksColumn && show('myTasks'));
    const showRightColumn = !!(
        (user?.can('change:read') && show('whatsNew'))
        || (user?.can('change:support:read') && (show('supportIndicators') || show('triageChanges')))
        || (user?.can('change:read') && show('teamReview'))
        || (user?.can('release:read') && show('lastReleases'))
        || ((!showLeftColumn) && user?.can('log:read') && show('lastActivity'))
    );

    const persistPrefs = (next: DashboardPreferences) => {
        if (!user?._id)
            return;
        dispatch(updateUser({
            _id: user._id,
            email: user.email,
            firstname: user.firstname,
            lastname: user.lastname,
            phone: user.phone,
            role: user.role,
            avatar: user.avatar,
            favoriteProjects: user.favoriteProjects,
            notificationPreferences: user.notificationPreferences,
            appearancePreferences: user.appearancePreferences,
            trackingPreferences: user.trackingPreferences,
            uiPreferences: user.uiPreferences,
            work_rate: user.work_rate,
            account_type: user.account_type,
            dashboardPreferences: next,
        }));
    };

    const onPresetChange = (preset: DashboardPreset) => {
        persistPrefs(preferencesForPreset(preset));
    };

    const onToggleWidget = (widgetId: DashboardWidgetId) => {
        persistPrefs(toggleWidgetHidden(prefs, widgetId));
    };

    document.title = 'Yoda';

    return (
        <div className="Dashboard">
            <header className="bg-white dark:bg-gray-900 border-b dark:border-gray-700 px-4 py-0 sm:px-6 lg:px-8">
                <div className="my-2 mx-auto lg:flex lg:items-center lg:justify-between gap-3">
                    <div>
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                            <Icon type="home" size={7} className="mr-1.5 inline" />
                            { t('app.dashboard') }
                        </h2>
                    </div>
                    {user?.role !== 'client' && (
                    <div className="flex flex-wrap items-center gap-2">
                        <label className="text-sm text-gray-600 dark:text-gray-300" htmlFor="dashboard-preset">
                            {t('dashboard.preset')}
                        </label>
                        <select
                            id="dashboard-preset"
                            className="rounded-md border-gray-300 dark:border-gray-600 text-sm"
                            value={prefs.preset}
                            onChange={(e) => onPresetChange(e.target.value as DashboardPreset)}
                        >
                            <option value="development">{t('dashboard.presets.development')}</option>
                            <option value="support">{t('dashboard.presets.support')}</option>
                            <option value="pilotage">{t('dashboard.presets.pilotage')}</option>
                            <option value="client">{t('dashboard.presets.client')}</option>
                            <option value="custom">{t('dashboard.presets.custom')}</option>
                        </select>
                        <button
                            type="button"
                            className="inline-flex items-center justify-center rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            onClick={() => setPrefsOpen((v) => !v)}
                            title={(prefsOpen ? t('dashboard.hide_widgets') : t('dashboard.customize_widgets')) as string}
                            aria-label={(prefsOpen ? t('dashboard.hide_widgets') : t('dashboard.customize_widgets')) as string}
                            aria-expanded={prefsOpen}
                        >
                            <Icon type={prefsOpen ? 'close' : 'setting'} size={4} />
                        </button>
                    </div>
                    )}
                </div>
                {user?.role !== 'client' && prefsOpen && (
                    <div className="mb-3 flex flex-wrap gap-3 rounded border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2">
                        {DASHBOARD_WIDGET_IDS.map((widgetId) => (
                            <label key={widgetId} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
                                <input
                                    type="checkbox"
                                    checked={show(widgetId)}
                                    onChange={() => onToggleWidget(widgetId)}
                                />
                                {t(`dashboard.widgets.${widgetId}`)}
                            </label>
                        ))}
                    </div>
                )}
            </header>
            <main>
                <DashboardBriefing
                    statistics={statistics}
                    showSprintInfo={show('sprintInfo')}
                    showKpis={show('briefingKpis')}
                    hiddenKpis={prefs.hiddenKpis}
                />
                {show('favorites') && (
                    <Ability can="project:read">
                        <FavoriteProjectsStrip />
                    </Ability>
                )}
                <div className="m-5 grid grid-cols-1 gap-3 2xl:grid-cols-4 2xl:items-start">
                    {showLeftColumn && (
                        <div className={`col-span-1 flex flex-col space-y-3 ${showRightColumn ? '2xl:col-span-3' : '2xl:col-span-4'}`}>
                            <MyTasksFocus />
                            {show('lastActivity') && (
                                <Ability can="log:read">
                                    <LastActivity
                                        withHeader
                                        limit={40}
                                        headerLinkTo="/users/activity"
                                        headerLinkLabel={t('dashboard.view_my_activity')}
                                    />
                                </Ability>
                            )}
                        </div>
                    )}
                    {showRightColumn && (
                    <div className={`col-span-1 flex flex-col space-y-3 ${showLeftColumn ? '2xl:col-span-1' : '2xl:col-span-4'}`}>
                        {show('whatsNew') && (
                            <Ability can="change:read">
                                <WhatsNew />
                            </Ability>
                        )}
                        {show('supportIndicators') && (
                            <Ability can="change:support:read">
                                <SupportIndicators />
                            </Ability>
                        )}
                        {show('triageChanges') && (
                            <Ability can="change:support:read">
                                <TriageChanges />
                            </Ability>
                        )}
                        {show('teamReview') && (
                            <Ability can="change:read">
                                <ChangesTeamReview />
                            </Ability>
                        )}
                        {show('lastReleases') && (
                            <Ability can="release:read">
                                <LastReleases withHeader />
                            </Ability>
                        )}
                        {!showLeftColumn && show('lastActivity') && (
                            <Ability can="log:read">
                                <LastActivity
                                    withHeader
                                    limit={40}
                                    headerLinkTo="/users/activity"
                                    headerLinkLabel={t('dashboard.view_my_activity')}
                                />
                            </Ability>
                        )}
                    </div>
                    )}
                </div>
            </main>
        </div>
    );
}

export default Dashboard;
